import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Z_INDEX } from '../../utils/constants/z-index.constant';

export interface BaoModalProps {
  /** Controls visibility of the modal */
  isOpen: boolean;
  /** Called when the modal should close (ESC, backdrop click, etc.) */
  onClose: () => void;
  /** Modal content – existing layout and styling live here */
  children: React.ReactNode;
  /** Close when pressing ESC (default: true) */
  closeOnEsc?: boolean;
  /** Close when clicking on the backdrop (default: true) */
  closeOnBackdropClick?: boolean;
  /** Optional className for the backdrop wrapper (fixed inset-0, z-index, flex, etc.) */
  backdropClassName?: string;
  /** Optional inline style for the backdrop wrapper (e.g. backgroundColor) */
  backdropStyle?: React.CSSProperties;
  /** Optional className for the inner panel container */
  panelClassName?: string;
  /** Optional inline style for the inner panel container */
  panelStyle?: React.CSSProperties;
  /** Render modal in a portal at document.body (default: true) for consistent layering */
  usePortal?: boolean;
  /** Use framer-motion for backdrop/panel animation (default: true). Set false to match legacy no-animation behavior. */
  animate?: boolean;
}

const DEFAULT_BACKDROP_CLASSES = 'fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm';

const BaoModal: React.FC<BaoModalProps> = ({
  isOpen,
  onClose,
  children,
  closeOnEsc = true,
  closeOnBackdropClick = true,
  backdropClassName,
  backdropStyle,
  panelClassName,
  panelStyle,
  usePortal = true,
  animate = true,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Handle ESC key to close the modal
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeOnEsc, onClose]);

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (!closeOnBackdropClick) return;
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const backdropClasses = backdropClassName || DEFAULT_BACKDROP_CLASSES;
  const BackdropWrapper = animate ? motion.div : 'div';
  const PanelWrapper = animate ? motion.div : 'div';
  const backdropMotionProps = animate
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.15 } }
    : {};
  const panelMotionProps = animate
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.15 } }
    : {};

  const content = (
    <BackdropWrapper
      className={backdropClasses}
      style={{ ...backdropStyle, zIndex: Z_INDEX.MODAL_BACKDROP }}
      onClick={handleBackdropClick}
      {...(BackdropWrapper === motion.div ? backdropMotionProps : {})}
    >
      <PanelWrapper
        ref={panelRef as React.Ref<HTMLDivElement>}
        className={panelClassName}
        style={panelStyle}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        {...(PanelWrapper === motion.div ? panelMotionProps : {})}
      >
        {children}
      </PanelWrapper>
    </BackdropWrapper>
  );

  if (usePortal && typeof document !== 'undefined' && document.body) {
    return createPortal(content, document.body);
  }

  return content;
};

export default BaoModal;

